// Airport density-altitude layer family.
//
// Renders a coloured halo behind each airport in the visible bbox,
// tinted by current density altitude at field elevation:
//
//   bucket 1 (≤1500 ft DA)      blue   #3b82f6
//   bucket 2 (1500-3500)        green  #22c55e
//   bucket 3 (3500-6500)        yellow #eab308
//   bucket 4 (6500-9500)        orange #f97316
//   bucket 5 (>9500)            red    #ef4444
//
// Source is /api/wx/airport-da?bbox=W,S,E,N — server computes DA per
// airport from the latest METAR (ISA fallback when no METAR). Buckets
// are emitted as `daBucket` properties so the paint expression is a
// simple step on a number rather than nested math.
//
// Boss request 2026-05-25 21:28 PT.

import type { Map as MlMap } from 'maplibre-gl';
import { EMPTY_FC } from '../MapView';
import { registerLayerFamily } from '../layerVisibilityRegistry';
import { useLayerVisibilityStore } from '../../stores/layerVisibilityStore';
import { isOverlayLoaded, markOverlayLoaded, refreshOverlay } from '../overlayRuntime';
import { OVERLAY_BY_ID } from '../overlaySpecs';

export function addAirportDALayers(map: MlMap): void {
  if (!map.getSource('airport-da')) {
    map.addSource('airport-da', { type: 'geojson', data: EMPTY_FC });
    map.addLayer({
      id: 'airport-da-halo',
      type: 'circle',
      source: 'airport-da',
      // z>=8 so we don't paint giant blobs at country-wide zoom. The
      // airport-dot layer typically becomes visible around z=7.
      minzoom: 8,
      layout: { visibility: 'none' },
      paint: {
        'circle-radius': [
          'interpolate', ['linear'], ['zoom'],
          8,  6,
          11, 10,
          14, 14,
        ],
        'circle-color': [
          'match',
          ['to-number', ['get', 'daBucket'], 2],
          1, '#3b82f6', // sea-level cool: blue
          2, '#22c55e', // normal: green
          3, '#eab308', // attention: yellow
          4, '#f97316', // marginal: orange
          5, '#ef4444', // hot+high: red
          '#22c55e',
        ],
        'circle-opacity': 0.75,
        'circle-stroke-color': '#0b0d0f',
        'circle-stroke-width': 1.5,
      },
    });
  }
}

registerLayerFamily({
  id: 'airport-da',
  label: 'Density Altitude',
  sourceId: 'airport-da',
  layerIds: ['airport-da-halo'],
  mount: (map) => addAirportDALayers(map),
  visibilitySelector: (l) => l['airport-da'],
  applyState: (map) => {
    if (!map.getLayer('airport-da-halo')) return;
    const show = useLayerVisibilityStore.getState().layers['airport-da'];
    map.setLayoutProperty('airport-da-halo', 'visibility', show ? 'visible' : 'none');
  },
  onToggleOn: (map) => {
    const spec = OVERLAY_BY_ID.get('airport-da');
    if (!spec || isOverlayLoaded('airport-da')) return;
    markOverlayLoaded('airport-da');
    return refreshOverlay(map, spec);
  },
  bboxRefresh: (map) => {
    if (!isOverlayLoaded('airport-da')) return;
    const spec = OVERLAY_BY_ID.get('airport-da');
    if (!spec) return;
    return refreshOverlay(map, spec);
  },
});
